import { SPEC_SOURCE, SPEC_STATUS } from '@services/scoreSpecs';
import { DATE_RANGE_PRESETS } from '@utils/dateRange';
import { createTimeInstance } from '@utils/time';
import { AUTHOR_ALL, AUTHOR_MINE, AUTHOR_SEED, EMPTY_LIST_FILTERS, LIVE_VALUES } from './listFilters';

// The list's filters as a query string, and back. One module so the two directions cannot drift:
// what serialize writes is exactly what parse reads.
//
// The URL is the list's filter state, not a copy of it — so a reload, a shared link and browser-back
// from a spec all land on the same view. Mirrors utils/documentVaultUrl.js, which does the same for
// the vault; not shared with it because the two filter shapes have nothing in common but a date
// window.

// What the list opens on when the URL says nothing: everyone's defaults, narrowed to the reader.
export const LIST_DEFAULT_FILTERS = { ...EMPTY_LIST_FILTERS, author: AUTHOR_MINE };

const PARAM_KEYS = {
  status: 'status',
  source: 'source',
  live: 'live',
  author: 'author',
  rangePreset: 'updated',
  rangeFrom: 'from',
  rangeTo: 'to',
  q: 'q',
};

// The list endpoint 422s on a status or source it does not know, so a hand-typed or stale value is
// dropped here rather than sent. SPEC_STATUS / SPEC_SOURCE are the service's own maps.
const STATUS_VALUES = new Set(['all', ...Object.values(SPEC_STATUS)]);
const SOURCE_VALUES = new Set(['all', ...Object.values(SPEC_SOURCE)]);
const PRESET_VALUES = new Set(Object.values(DATE_RANGE_PRESETS));
// Client-side, so an unknown value could not reach the server anyway; it still falls back to All
// rather than becoming a filter that matches nothing.
const LIVE_VALUE_SET = new Set(LIVE_VALUES);

const oneOf = (value, allowed, fallback) => (value && allowed.has(value) ? value : fallback);

// An author is one of the three buckets or a user id. Anything else would be a filter that matches
// nothing and names nobody.
const authorOf = (value, fallback) => {
  if (!value) return fallback;
  if (value === AUTHOR_ALL || value === AUTHOR_MINE || value === AUTHOR_SEED || /^\d+$/.test(value)) return value;
  return fallback;
};

// Bare 'YYYY-MM-DD', and a real date. dayjs rolls an overflow forward rather than rejecting it —
// '2026-13-45' parses as a day in early 2027 — so the check is that the value survives a round trip.
const isoDateOf = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const instance = createTimeInstance(value);
  return instance && instance.isValid() && instance.format('YYYY-MM-DD') === value ? value : null;
};

// Takes a query string (with or without the '?') or a URLSearchParams. Unknown values fall back to
// the default for that key rather than emptying the list; unknown keys are ignored.
export const parseListFilters = (search, defaults = LIST_DEFAULT_FILTERS) => {
  const params = new URLSearchParams(search || '');
  const rangePreset = oneOf(params.get(PARAM_KEYS.rangePreset), PRESET_VALUES, defaults.rangePreset);
  // Dates mean something only under Custom (utils/dateRange.js); under any other preset they are
  // leftovers and would re-apply a window the operator had moved on from.
  const isCustom = rangePreset === DATE_RANGE_PRESETS.custom;
  return {
    status: oneOf(params.get(PARAM_KEYS.status), STATUS_VALUES, defaults.status),
    source: oneOf(params.get(PARAM_KEYS.source), SOURCE_VALUES, defaults.source),
    live: oneOf(params.get(PARAM_KEYS.live), LIVE_VALUE_SET, defaults.live),
    q: params.get(PARAM_KEYS.q) || defaults.q,
    author: authorOf(params.get(PARAM_KEYS.author), defaults.author),
    rangePreset,
    rangeFrom: isCustom ? isoDateOf(params.get(PARAM_KEYS.rangeFrom)) : null,
    rangeTo: isCustom ? isoDateOf(params.get(PARAM_KEYS.rangeTo)) : null,
  };
};

// Fixed key order, defaults omitted: the same view always produces the same URL, and the default
// view produces none. Returns URLSearchParams — what setSearchParams takes — and .toString() is the
// query string without the '?'.
export const serializeListFilters = (filters, defaults = LIST_DEFAULT_FILTERS) => {
  const params = new URLSearchParams();
  const setUnlessDefault = (key) => {
    if (filters[key] !== defaults[key]) params.set(PARAM_KEYS[key], filters[key]);
  };

  setUnlessDefault('status');
  setUnlessDefault('source');
  setUnlessDefault('live');
  setUnlessDefault('author');

  if (filters.rangePreset && filters.rangePreset !== defaults.rangePreset) {
    params.set(PARAM_KEYS.rangePreset, filters.rangePreset);
    if (filters.rangePreset === DATE_RANGE_PRESETS.custom) {
      if (filters.rangeFrom) params.set(PARAM_KEYS.rangeFrom, filters.rangeFrom);
      if (filters.rangeTo) params.set(PARAM_KEYS.rangeTo, filters.rangeTo);
    }
  }

  // Raw, not trimmed: the search box is bound to this value, and trimming here would eat the space
  // the operator just typed. isFiltering trims where it matters.
  if (filters.q) params.set(PARAM_KEYS.q, filters.q);

  return params;
};
